'use client';
import { createBrowserClient } from '@supabase/ssr';

// ─── Module-level console.error suppression ───────────────────────────────────
// Supabase's internal auth initialization fires console.error for rate-limit
// and network errors BEFORE any useEffect-level patches can be applied.
// This patch is applied at module load time so it's always in place first.
if (typeof window !== 'undefined') {
  const _orig = console.error;
  const SUPPRESSED = [
    'rate limit',
    'request rate limit',
    'rate limit reached',
    'over_request_rate_limit',
    'too many requests',
    'autoapierror',
    'failed to fetch',
    'networkerror',
    'network request failed',
  ];
  console.error = (...args: any[]) => {
    const msg = args
      .map((a) => {
        if (a && typeof a === 'object') {
          return String(a?.message || a?.name || a?.code || JSON.stringify(a) || '');
        }
        return String(a || '');
      })
      .join(' ')
      .toLowerCase();
    if (SUPPRESSED.some((p) => msg.includes(p))) return;
    _orig.apply(console, args);
  };
}

// Singleton instance — reuse the same client across all components to prevent
// multiple auth refresh timers from running simultaneously.
let _clientInstance: ReturnType<typeof createBrowserClient> | null = null;

export function createClient() {
  if (_clientInstance) return _clientInstance;

  _clientInstance = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    }
  );

  return _clientInstance;
}